import { getAuthHeaders } from "../../../../utils/authUtils";
import { API_BASE_URL } from "../../../../environment";

export interface EmailMessage {
  mail_message_id: number;
  content: string;
  subject: string;
  send_to: string;
  send_from: string;
  is_important: boolean;
  is_read: boolean;
  is_archive: boolean;
  is_trash: boolean;
  email_datetime: string;
  created_datetime: string;
  mail_cc: string;
  message_id: string;
  attachments?: Array<{
    id: number;
    attachement_url?: string;
    attachment_url?: string;
    attachment_link?: string | null;
    size_in_kb?: number;
  }>;
  mail_auth_id: {
    mail_auth_id: number;
    email_address: string;
    access_token: string;
    refresh_token: string;
    expiration_datetime: string;
    created_datetime: string;
  };
  mail_message_type_id: {
    mail_message_type_id: number;
    mail_message_type_display_name: string;
    mail_message_type_key: string;
  };
  mail_category_id: {
    mail_category_id: number;
    mail_category_display_name: string;
    mail_category_key: string;
  };
  mail_provider_key: string;
  crm_entity?: {
    crm_entity_id: number;
    crm_entity_key: string;
    crm_entity_display_name: string;
  };
}

export interface MailProvider {
  mail_provider_id: number;
  mail_provider_display_name: string;
  mail_provider_key: string;
  mail_provider_logo: string | null;
  connected: boolean;
  email?: string;
  mail_auth_id?: number;
  stats?: {
    inbox: number;
    important: number;
    sentMail: number;
    drafts: number;
    spam: number;
    trash: number;
  };
  email_builtin_folder_stats?: {
    inbox: number;
    important: number;
    sentMail: number;
    drafts: number;
    spam: number;
    trash: number;
  };
  email_custom_folder_stats?: {
    [key: string]: number;
  };
}

export interface MailMessageType {
  mail_message_type_id: number;
  mail_message_type_display_name: string;
  mail_message_type_key: string;
}

export interface MailCategory {
  mail_category_id: number;
  mail_category_display_name: string;
  mail_category_key: string;
}

export interface UpdateEmailStatusPayload {
  mail_message_type_id?: number;
  mail_category_id?: number;
  is_archive?: boolean;
  is_read?: boolean;
  is_important?: boolean;
  is_trash?: boolean;
}

export interface SendEmailPayload {
  to: string[];
  cc?: string[];
  subject: string;
  body: string;
  is_html: boolean;
  user_id: string;
  crm_entity_id?: number;
  record_id?: number;
  attachments?: Array<{
    filename: string;
    content: string;
    content_type: string;
  }>;
  attachment_links?: string[];
}

export interface SaveDraftPayload {
  to: string[];
  cc?: string[];
  subject: string;
  body: string;
  user_id: string;
  providerKey: string;
}

export interface BulkMailOperationPayload {
  mail_message_ids: number[];
  operation:
  | "mark_read"
  | "mark_unread"
  | "delete"
  | "trash"
  | "untrash"
  | "mark_important"
  | "unmark_important";
}

export interface EmailResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: EmailMessage[];
}

export interface PaginatedEmailResponse {
  emails: EmailMessage[];
  totalCount: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

class EmailService {
  private baseUrl = API_BASE_URL;
  private cachedEmails: EmailMessage[] = [];
  private isDataLoaded = false;
  private totalApiCount = 0;
  private nextPageUrl: string | null = null;
  private currentApiPage = 1;
  private cachedProviders: MailProvider[] | null = null;
  private cachedProviderUserId: string | null = null;
  private cachedTenantId: string | null = null;
  private cachedEmailPages = new Map<string, any>();
  private mailProvidersPromise: Promise<MailProvider[]> | null = null;
  private mailProvidersPromiseKey: string | null = null;

  private getProviderUrlKey(providerKey: string): string {
    const providerMap: { [key: string]: string } = {
      MICROSOFT_365: "microsoft",
      GOOGLE: "google",
    };

    return providerMap[providerKey.toUpperCase()] || providerKey.toLowerCase();
  }

  async getInitialEmails(userId: string): Promise<EmailMessage[]> {
    if (this.isDataLoaded) {
      return this.cachedEmails;
    }

    try {
      const headers = await getAuthHeaders();
      const tenantId = localStorage.getItem("selectedTenantId") || "";
      const response = await fetch(
        `${this.baseUrl}/mail-message/?user_id=${userId}&tenant_id=${tenantId}`,
        {
          method: "GET",
          headers: {
            ...headers,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data || typeof data !== "object") {
        throw new Error("Invalid response format");
      }

      this.cachedEmails = data.results || [];
      this.totalApiCount = data.count || 0;
      this.nextPageUrl = data.next || null;
      this.currentApiPage = 1;
      this.isDataLoaded = true;

      return this.cachedEmails;
    } catch (error) {
      console.error("Error fetching initial emails:", error);
      throw error;
    }
  }


  clearEmailCache() {
    this.cachedEmailPages.clear();
  }

  async getEmailsByType(
    userId: string,
    type: string,
    page: number = 1,
    pageSize: number = 25,
    forceRefresh: boolean = false
  ): Promise<PaginatedEmailResponse> {
    const tenantId = localStorage.getItem("selectedTenantId") || "";
    const cacheKey = `${tenantId}-${userId}-${type}-${page}-${pageSize}`;
    if (!forceRefresh && this.cachedEmailPages.has(cacheKey)) {
      return this.cachedEmailPages.get(cacheKey);
    }

    try {
      const headers = await getAuthHeaders();
      const tenantId = localStorage.getItem("selectedTenantId") || "";

      let url = `${this.baseUrl}/mail-message/?page=${page}&page_size=${pageSize}&user_id=${userId}&tenant_id=${tenantId}`;
      if (type.toLowerCase() === 'trash') {
        url += '&is_trash=true';
      } else {
        url += '&is_trash=false';
        if (type.toLowerCase() === 'important') {
          url += `&is_important=true`;
        } else {
          url += `&mail_message_type_key=${type.toUpperCase()}`;
        }
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          ...headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data || typeof data !== "object") {
        throw new Error("Invalid response format");
      }

      let allEmails = [];
      if (data && Array.isArray(data.results)) {
        allEmails = data.results;
      } else if (data && Array.isArray(data.data)) {
        allEmails = data.data;
      } else if (Array.isArray(data)) {
        allEmails = data;
      }

      const totalCount = data.count || 0;
      const hasNextPage = data.next !== null;
      const hasPreviousPage = page > 1;

      const result = {
        emails: allEmails,
        totalCount: totalCount,
        currentPage: page,
        hasNextPage: hasNextPage,
        hasPreviousPage: hasPreviousPage,
      };
      this.cachedEmailPages.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error(`Error fetching ${type} emails:`, error);
      throw error;
    }
  }


  async searchEmailsByType(
    userId: string,
    type: string,
    query: string,
    page: number = 1,
    pageSize: number = 25
  ): Promise<PaginatedEmailResponse> {
    try {
      const headers = await getAuthHeaders();
      const tenantId = localStorage.getItem("selectedTenantId") || "";
      let searchUrl = `${this.baseUrl}/mail-message/?page=${page}&page_size=${pageSize}&user_id=${userId}&tenant_id=${tenantId}&content=${encodeURIComponent(query)}`;
      if (type.toLowerCase() === 'trash') {
        searchUrl += '&is_trash=true';
      } else {
        searchUrl += '&is_trash=false';
        if (type.toLowerCase() === 'important') {
          searchUrl += `&is_important=true`;
        } else {
          searchUrl += `&mail_message_type_key=${type.toUpperCase()}`;
        }
      }

      const response = await fetch(searchUrl, {
        method: "GET",
        headers: {
          ...headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      let searchResults = [];
      if (data && Array.isArray(data.results)) {
        searchResults = data.results;
      } else if (data && Array.isArray(data.data)) {
        searchResults = data.data;
      } else if (Array.isArray(data)) {
        searchResults = data;
      }

      const totalCount = data.count || 0;
      const hasNextPage = data.next !== null;
      const hasPreviousPage = page > 1;

      return {
        emails: searchResults,
        totalCount: totalCount,
        currentPage: page,
        hasNextPage: hasNextPage,
        hasPreviousPage: hasPreviousPage,
      };
    } catch (error) {
      console.error(`Error searching ${type} emails:`, error);
      throw error;
    }
  }

  async getEmailsByFolder(
    folderId: number,
    page: number = 1,
    pageSize: number = 25,
    forceRefresh: boolean = false
  ): Promise<PaginatedEmailResponse> {
    const cacheKey = `folder-${folderId}-${page}-${pageSize}`;
    if (!forceRefresh && this.cachedEmailPages.has(cacheKey)) {
      return this.cachedEmailPages.get(cacheKey);
    }

    try {
      const headers = await getAuthHeaders();
      const tenantId = localStorage.getItem("selectedTenantId") || "";

      const url = `${this.baseUrl}/mail-message/?page=${page}&page_size=${pageSize}&tenant_id=${tenantId}&mail_auth_folder_id=${folderId}&is_trash=false`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          ...headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data || typeof data !== "object") {
        throw new Error("Invalid response format");
      }

      let allEmails = [];
      if (data && Array.isArray(data.results)) {
        allEmails = data.results;
      } else if (data && Array.isArray(data.data)) {
        allEmails = data.data;
      } else if (Array.isArray(data)) {
        allEmails = data;
      }

      const totalCount = data.count || 0;
      const hasNextPage = data.next !== null;
      const hasPreviousPage = page > 1;

      const result = {
        emails: allEmails,
        totalCount: totalCount,
        currentPage: page,
        hasNextPage: hasNextPage,
        hasPreviousPage: hasPreviousPage,
      };
      this.cachedEmailPages.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error(`Error fetching folder ${folderId} emails:`, error);
      throw error;
    }
  }

  async searchEmailsByFolder(
    folderId: number,
    query: string,
    page: number = 1,
    pageSize: number = 25
  ): Promise<PaginatedEmailResponse> {
    try {
      const headers = await getAuthHeaders();
      const tenantId = localStorage.getItem("selectedTenantId") || "";
      const searchUrl = `${this.baseUrl}/mail-message/?page=${page}&page_size=${pageSize}&tenant_id=${tenantId}&mail_auth_folder_id=${folderId}&is_trash=false&content=${encodeURIComponent(query)}`;

      const response = await fetch(searchUrl, {
        method: "GET",
        headers: {
          ...headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      let searchResults = [];
      if (data && Array.isArray(data.results)) {
        searchResults = data.results;
      } else if (data && Array.isArray(data.data)) {
        searchResults = data.data;
      } else if (Array.isArray(data)) {
        searchResults = data;
      }

      const totalCount = data.count || 0;
      const hasNextPage = data.next !== null;
      const hasPreviousPage = page > 1;

      return {
        emails: searchResults,
        totalCount: totalCount,
        currentPage: page,
        hasNextPage: hasNextPage,
        hasPreviousPage: hasPreviousPage,
      };
    } catch (error) {
      console.error(`Error searching folder ${folderId} emails:`, error);
      throw error;
    }
  }

  async getMailConversation(
    userId: string,
    contactIds: number[],
    page: number = 1,
    pageSize: number = 25
  ): Promise<PaginatedEmailResponse> {
    try {
      const headers = await getAuthHeaders();
      const tenantId = localStorage.getItem("selectedTenantId") || "";

      const apiPage = Math.ceil(page / 2);

      const payload = {
        user_id: userId,
        contact_ids: contactIds,
        tenant_id: tenantId,
      };

      const response = await fetch(
        `${this.baseUrl}/mail-message/mail-conversation/?page=${apiPage}`,
        {
          method: "POST",
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data || typeof data !== "object") {
        throw new Error("Invalid response format");
      }

      const allEmails = data.results || [];
      const totalCount = data.count || 0;

      const startIndex = ((page - 1) % 2) * 25;
      const endIndex = startIndex + 25;
      const conversationEmails = allEmails.slice(startIndex, endIndex);

      const hasMoreRecordsInCurrentPage = endIndex < allEmails.length;

      const hasNextApiPage = data.next !== null;

      const currentTotalShown = (apiPage - 1) * 50 + endIndex;
      const hasReachedTotal = currentTotalShown >= totalCount;

      const hasNextPage =
        hasMoreRecordsInCurrentPage || (hasNextApiPage && !hasReachedTotal);
      const hasPreviousPage = page > 1;

      return {
        emails: conversationEmails,
        totalCount: totalCount,
        currentPage: page,
        hasNextPage: hasNextPage,
        hasPreviousPage: hasPreviousPage,
      };
    } catch (error) {
      console.error("Error fetching mail conversation:", error);
      throw error;
    }
  }

  async loadNextPage(): Promise<EmailMessage[]> {
    if (!this.nextPageUrl) {
      return this.cachedEmails;
    }

    try {
      const headers = await getAuthHeaders();
      const tenantId = localStorage.getItem("selectedTenantId") || "";
      const url = new URL(this.nextPageUrl);
      url.searchParams.set("tenant_id", tenantId);
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          ...headers,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data || typeof data !== "object") {
        throw new Error("Invalid response format");
      }

      const newEmails = data.results || [];
      this.cachedEmails = [...this.cachedEmails, ...newEmails];

      this.nextPageUrl = data.next || null;
      this.currentApiPage++;

      return this.cachedEmails;
    } catch (error) {
      console.error("Error fetching next page:", error);
      throw error;
    }
  }

  async refreshEmails(userId: string): Promise<EmailMessage[]> {
    try {
      const headers = await getAuthHeaders();
      const tenantId = localStorage.getItem("selectedTenantId") || "";
      const response = await fetch(
        `${this.baseUrl}/mail-message/?user_id=${userId}&tenant_id=${tenantId}`,
        {
          method: "GET",
          headers: {
            ...headers,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data || typeof data !== "object") {
        throw new Error("Invalid response format");
      }

      this.cachedEmails = data.results || [];
      this.totalApiCount = data.count || 0;
      this.nextPageUrl = data.next || null;
      this.currentApiPage = 1;
      this.isDataLoaded = true;

      return this.cachedEmails;
    } catch (error) {
      console.error("Error refreshing emails:", error);
      throw error;
    }
  }

  async markEmailAsRead(mailMessageId: number): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      headers["Content-Type"] = "application/json";
      const tenantId = localStorage.getItem("selectedTenantId") || "";
      const response = await fetch(
        `${this.baseUrl}/mail-message/${mailMessageId}/?tenant_id=${tenantId}`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({ is_read: true }),
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const idx = this.cachedEmails.findIndex(
        (e) => e.mail_message_id === mailMessageId
      );
      if (idx !== -1) {
        this.cachedEmails[idx].is_read = true;
      }
      for (const pageData of Array.from(this.cachedEmailPages.values())) {
        if (pageData && Array.isArray(pageData.emails)) {
          const pageIdx = pageData.emails.findIndex((e: any) => e.mail_message_id === mailMessageId);
          if (pageIdx !== -1) {
            pageData.emails[pageIdx].is_read = true;
          }
        }
      }
    } catch (error) {
      console.error("Error marking email as read:", error);
    }
  }

  async getEmailById(mailMessageId: number): Promise<EmailMessage> {
    try {
      const headers = await getAuthHeaders();
      const tenantId = localStorage.getItem("selectedTenantId") || "";
      const response = await fetch(
        `${this.baseUrl}/mail-message/${mailMessageId}/?tenant_id=${tenantId}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching email by ID:", error);
      throw error;
    }
  }

  async toggleEmailImportance(mailMessageId: number, currentImportanceStatus: boolean): Promise<boolean> {
    try {
      const newImportanceStatus = !currentImportanceStatus;

      await this.updateEmailStatus(mailMessageId, {
        is_important: newImportanceStatus,
      });

      return newImportanceStatus;
    } catch (error) {
      console.error("Error toggling email importance:", error);
      throw error;
    }
  }

  getInboxEmails(
    page: number = 1,
    pageSize: number = 25
  ): PaginatedEmailResponse {
    const inboxEmails = this.cachedEmails.filter(
      (email) =>
        email &&
        email.mail_message_type_id &&
        email.mail_message_type_id.mail_message_type_key === "INBOX" &&
        !email.is_trash
    );

    return this.paginateEmails(inboxEmails, page, pageSize);
  }

  getImportantEmails(
    page: number = 1,
    pageSize: number = 25
  ): PaginatedEmailResponse {
    const importantEmails = this.cachedEmails.filter(
      (email) => email && email.is_important === true && !email.is_trash
    );

    return this.paginateEmails(importantEmails, page, pageSize);
  }

  getSentEmails(
    page: number = 1,
    pageSize: number = 25
  ): PaginatedEmailResponse {
    const sentEmails = this.cachedEmails.filter(
      (email) =>
        email &&
        email.mail_message_type_id &&
        email.mail_message_type_id.mail_message_type_key === "SENT" &&
        !email.is_trash
    );

    return this.paginateEmails(sentEmails, page, pageSize);
  }

  getUnreadInboxCount(): number {
    return this.cachedEmails.filter(
      (email) =>
        email &&
        email.mail_message_type_id &&
        email.mail_message_type_id.mail_message_type_key === "INBOX" &&
        !email.is_read &&
        !email.is_trash
    ).length;
  }

  getUnreadImportantCount(): number {
    return this.cachedEmails.filter(
      (email) =>
        email &&
        email.is_important === true &&
        !email.is_read &&
        !email.is_trash
    ).length;
  }

  getTotalApiCount(): number {
    return this.totalApiCount;
  }

  getAllCachedEmails(): EmailMessage[] {
    return this.cachedEmails;
  }

  getInboxCount(): number {
    return this.cachedEmails.filter(
      (email) =>
        email &&
        email.mail_message_type_id &&
        email.mail_message_type_id.mail_message_type_key === "INBOX" &&
        !email.is_trash
    ).length;
  }

  getImportantCount(): number {
    return this.cachedEmails.filter(
      (email) => email && email.is_important === true && !email.is_trash
    ).length;
  }

  getSentCount(): number {
    return this.cachedEmails.filter(
      (email) =>
        email &&
        email.mail_message_type_id &&
        email.mail_message_type_id.mail_message_type_key === "SENT" &&
        !email.is_trash
    ).length;
  }

  getTrashEmails(
    page: number = 1,
    pageSize: number = 25
  ): PaginatedEmailResponse {
    const trashEmails = this.cachedEmails.filter(
      (email) => email && email.is_trash === true
    );

    return this.paginateEmails(trashEmails, page, pageSize);
  }

  getTrashCount(): number {
    return this.cachedEmails.filter((email) => email && email.is_trash === true)
      .length;
  }

  hasMorePages(): boolean {
    return this.nextPageUrl !== null;
  }

  private paginateEmails(
    emails: EmailMessage[],
    page: number,
    pageSize: number
  ): PaginatedEmailResponse {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedEmails = emails.slice(startIndex, endIndex);

    const needsMoreData = endIndex >= emails.length && this.hasMorePages();

    return {
      emails: paginatedEmails,
      totalCount: emails.length,
      currentPage: page,
      hasNextPage: endIndex < emails.length || needsMoreData,
      hasPreviousPage: page > 1,
    };
  }

  clearCache(): void {
    this.cachedEmails = [];
    this.isDataLoaded = false;
    this.totalApiCount = 0;
    this.nextPageUrl = null;
    this.currentApiPage = 1;
  }

  getCachedProviders(userId: string): MailProvider[] | null {
    const currentTenantId = localStorage.getItem("selectedTenantId") || "";
    if (this.cachedProviders && this.cachedProviderUserId === userId && this.cachedTenantId === currentTenantId) {
      return this.cachedProviders;
    }
    return null;
  }

  async getMailProviders(userId: string, forceRefresh: boolean = false): Promise<MailProvider[]> {
    const currentTenantId = localStorage.getItem("selectedTenantId") || "";
    if (!forceRefresh && this.cachedProviders && this.cachedProviderUserId === userId && this.cachedTenantId === currentTenantId) {
      return this.cachedProviders;
    }

    const promiseKey = `${userId}-${currentTenantId}`;
    if (!forceRefresh && this.mailProvidersPromise && this.mailProvidersPromiseKey === promiseKey) {
      return this.mailProvidersPromise;
    }

    this.mailProvidersPromiseKey = promiseKey;
    this.mailProvidersPromise = (async () => {
      try {
        const headers = await getAuthHeaders();
        const tenantId = localStorage.getItem("selectedTenantId") || "";
        const response = await fetch(
          `${this.baseUrl}/lookup/mail-stats/?user_id=${userId}&tenant_id=${tenantId}&is_read=false`,
          {
            method: "GET",
            headers: {
              ...headers,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data || !Array.isArray(data.mail_provider)) {
          throw new Error("Invalid response format - expected array in mail_provider");
        }

        const connectedProviders = data.mail_provider.filter(
          (provider: MailProvider) => provider.connected === true
        );
        this.cachedProviders = connectedProviders;
        this.cachedProviderUserId = userId;
        this.cachedTenantId = currentTenantId;
        return connectedProviders;
      } catch (error) {
        console.error("Error fetching mail providers:", error);
        throw error;
      } finally {
        this.mailProvidersPromise = null;
        this.mailProvidersPromiseKey = null;
      }
    })();

    return this.mailProvidersPromise;
  }

  async sendEmail(
    providerKey: string,
    payload: SendEmailPayload
  ): Promise<void> {
    const headers = await getAuthHeaders();
    const tenantId = localStorage.getItem("selectedTenantId") || "";

    if (payload.attachments && payload.attachments.length > 0) {
      headers["Content-Type"] = "application/json";

      const sendPayload = {
        to: payload.to,
        cc: payload.cc,
        subject: payload.subject,
        body: payload.body,
        is_html: payload.is_html,
        user_id: payload.user_id,
        attachments: payload.attachments,
        attachment_links: payload.attachment_links || [],
        crm_entity_id: payload.crm_entity_id,
        record_id: payload.record_id,
      };

      const response = await fetch(
        `${this.baseUrl}/mail/${this.getProviderUrlKey(
          providerKey
        )}/send-email/?tenant_id=${tenantId}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(sendPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } else {
      headers["Content-Type"] = "application/json";

      const sendPayload = {
        to: payload.to,
        cc: payload.cc,
        subject: payload.subject,
        body: payload.body,
        is_html: payload.is_html,
        user_id: payload.user_id,
        attachment_links: payload.attachment_links || [],
        crm_entity_id: payload.crm_entity_id,
        record_id: payload.record_id,
      };

      const response = await fetch(
        `${this.baseUrl}/mail/${this.getProviderUrlKey(
          providerKey
        )}/send-email/?tenant_id=${tenantId}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(sendPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }
  }

  async sendEmailWithBinaryAttachments(
    providerKey: string,
    formData: FormData
  ): Promise<void> {
    const headers = await getAuthHeaders();
    const tenantId = localStorage.getItem("selectedTenantId") || "";

    const requestHeaders: Record<string, string> = {};
    for (const [key, value] of Object.entries(headers)) {
      if (key !== "Content-Type") {
        requestHeaders[key] = value as string;
      }
    }

    const entries = Array.from(formData.entries());

    const response = await fetch(
      `${this.baseUrl}/mail/${this.getProviderUrlKey(
        providerKey
      )}/send-email/?tenant_id=${tenantId}`,
      {
        method: "POST",
        headers: requestHeaders,
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  async saveDraft(payload: SaveDraftPayload): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      headers["Content-Type"] = "application/json";
      const tenantId = localStorage.getItem("selectedTenantId") || "";

      const hasHtmlTags = /<[^>]+>/.test(payload.body);

      const draftPayload = {
        to: payload.to,
        cc: payload.cc,
        subject: payload.subject,
        body: payload.body,
        is_html: hasHtmlTags,
        user_id: payload.user_id,
        mail_message_type_id: 3,
      };

      const response = await fetch(
        `${this.baseUrl}/mail/${this.getProviderUrlKey(
          payload.providerKey
        )}/send-email/?tenant_id=${tenantId}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(draftPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error saving draft:", error);
      throw error;
    }
  }

  async updateDraft(
    mailMessageId: number,
    payload: SendEmailPayload
  ): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      headers["Content-Type"] = "application/json";
      const tenantId = localStorage.getItem("selectedTenantId") || "";

      const updatePayload = {
        to: payload.to,
        cc: payload.cc,
        subject: payload.subject,
        body: payload.body,
        is_html: payload.is_html,
        user_id: payload.user_id,
        attachments: payload.attachments || [],
        attachment_links: payload.attachment_links || [],
      };

      const response = await fetch(
        `${this.baseUrl}/mail-message/${mailMessageId}/?tenant_id=${tenantId}`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(updatePayload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error updating draft:", error);
      throw error;
    }
  }

  async sendDraft(
    providerKey: string,
    payload: SendEmailPayload
  ): Promise<void> {
    const headers = await getAuthHeaders();
    const tenantId = localStorage.getItem("selectedTenantId") || "";

    if (payload.attachments && payload.attachments.length > 0) {
      headers["Content-Type"] = "application/json";

      const sendPayload = {
        to: payload.to,
        cc: payload.cc,
        subject: payload.subject,
        body: payload.body,
        is_html: payload.is_html,
        user_id: payload.user_id,
        attachments: payload.attachments,
        attachment_links: payload.attachment_links || [],
        mail_message_id: (payload as any).mail_message_id || null,
        crm_entity_id: payload.crm_entity_id,
        record_id: payload.record_id,
      };

      const response = await fetch(
        `${this.baseUrl}/mail-message/send-draft/?tenant_id=${tenantId}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(sendPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } else {
      headers["Content-Type"] = "application/json";

      const sendPayload = {
        to: payload.to,
        cc: payload.cc,
        subject: payload.subject,
        body: payload.body,
        is_html: payload.is_html,
        user_id: payload.user_id,
        attachment_links: payload.attachment_links || [],
        mail_message_id: (payload as any).mail_message_id || null,
        crm_entity_id: payload.crm_entity_id,
        record_id: payload.record_id,
      };

      const response = await fetch(
        `${this.baseUrl}/mail-message/send-draft/?tenant_id=${tenantId}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(sendPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }
  }

  async sendDraftWithBinaryAttachments(formData: FormData): Promise<void> {
    const headers = await getAuthHeaders();
    const tenantId = localStorage.getItem("selectedTenantId") || "";

    const requestHeaders: Record<string, string> = {};
    for (const [key, value] of Object.entries(headers)) {
      if (key !== "Content-Type") {
        requestHeaders[key] = value as string;
      }
    }

    const entries = Array.from(formData.entries());

    const response = await fetch(
      `${this.baseUrl}/mail-message/send-draft/?tenant_id=${tenantId}`,
      {
        method: "POST",
        headers: requestHeaders,
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  async updateDraftWithBinaryAttachments(
    mailMessageId: number,
    formData: FormData
  ): Promise<void> {
    const headers = await getAuthHeaders();
    const tenantId = localStorage.getItem("selectedTenantId") || "";

    const requestHeaders: Record<string, string> = {};
    for (const [key, value] of Object.entries(headers)) {
      if (key !== "Content-Type") {
        requestHeaders[key] = value as string;
      }
    }

    const entries = Array.from(formData.entries());

    const response = await fetch(
      `${this.baseUrl}/mail-message/${mailMessageId}/?tenant_id=${tenantId}`,
      {
        method: "PATCH",
        headers: requestHeaders,
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  async getMailMessageTypes(): Promise<MailMessageType[]> {
    try {
      const headers = await getAuthHeaders();
      const tenantId = localStorage.getItem("selectedTenantId") || "";
      const response = await fetch(
        `${this.baseUrl}/mail-message-type/?tenant_id=${tenantId}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error("Error fetching mail message types:", error);
      throw error;
    }
  }

  async getMailCategories(): Promise<MailCategory[]> {
    try {
      const headers = await getAuthHeaders();
      const tenantId = localStorage.getItem("selectedTenantId") || "";
      const response = await fetch(
        `${this.baseUrl}/mail-category/?tenant_id=${tenantId}`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error("Error fetching mail categories:", error);
      throw error;
    }
  }

  async updateEmailStatus(
    mailMessageId: number,
    payload: UpdateEmailStatusPayload
  ): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      headers["Content-Type"] = "application/json";
      const tenantId = localStorage.getItem("selectedTenantId") || "";

      const response = await fetch(
        `${this.baseUrl}/mail-message/${mailMessageId}/?tenant_id=${tenantId}`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const idx = this.cachedEmails.findIndex(
        (e) => e.mail_message_id === mailMessageId
      );
      if (idx !== -1) {
        const updatedEmail = { ...this.cachedEmails[idx] };

        if (payload.mail_message_type_id !== undefined) {
          const messageTypes = await this.getMailMessageTypes();
          const messageType = messageTypes.find(
            (mt) => mt.mail_message_type_id === payload.mail_message_type_id
          );
          if (messageType) {
            updatedEmail.mail_message_type_id = messageType;
          }
        }

        if (payload.mail_category_id !== undefined) {
          const categories = await this.getMailCategories();
          const category = categories.find(
            (cat) => cat.mail_category_id === payload.mail_category_id
          );
          if (category) {
            updatedEmail.mail_category_id = category;
          }
        }

        if (payload.is_archive !== undefined) {
          updatedEmail.is_archive = payload.is_archive;
        }

        if (payload.is_read !== undefined) {
          updatedEmail.is_read = payload.is_read;
        }

        if (payload.is_important !== undefined) {
          updatedEmail.is_important = payload.is_important;
        }

        if (payload.is_trash !== undefined) {
          updatedEmail.is_trash = payload.is_trash;
        }

        this.cachedEmails[idx] = updatedEmail;
      }

      for (const pageData of Array.from(this.cachedEmailPages.values())) {
        if (pageData && Array.isArray(pageData.emails)) {
          const pageIdx = pageData.emails.findIndex((e: any) => e.mail_message_id === mailMessageId);
          if (pageIdx !== -1) {
            const updatedEmail = { ...pageData.emails[pageIdx] };

            if (payload.mail_message_type_id !== undefined) {
              const messageTypes = await this.getMailMessageTypes();
              const messageType = messageTypes.find(
                (mt) => mt.mail_message_type_id === payload.mail_message_type_id
              );
              if (messageType) {
                updatedEmail.mail_message_type_id = messageType;
              }
            }

            if (payload.mail_category_id !== undefined) {
              const categories = await this.getMailCategories();
              const category = categories.find(
                (cat) => cat.mail_category_id === payload.mail_category_id
              );
              if (category) {
                updatedEmail.mail_category_id = category;
              }
            }

            if (payload.is_archive !== undefined) {
              updatedEmail.is_archive = payload.is_archive;
            }

            if (payload.is_read !== undefined) {
              updatedEmail.is_read = payload.is_read;
            }

            if (payload.is_important !== undefined) {
              updatedEmail.is_important = payload.is_important;
            }

            if (payload.is_trash !== undefined) {
              updatedEmail.is_trash = payload.is_trash;
            }

            pageData.emails[pageIdx] = updatedEmail;
          }
        }
      }
    } catch (error) {
      console.error("Error updating email status:", error);
      throw error;
    }
  }

  async moveToSpam(mailMessageId: number): Promise<void> {
    try {
      const messageTypes = await this.getMailMessageTypes();
      const spamType = messageTypes.find(
        (mt) => mt.mail_message_type_key === "SPAM"
      );

      if (!spamType) {
        throw new Error("Spam message type not found");
      }

      await this.updateEmailStatus(mailMessageId, {
        mail_message_type_id: spamType.mail_message_type_id,
      });
    } catch (error) {
      console.error("Error moving email to spam:", error);
      throw error;
    }
  }

  async moveToTrash(
    mailMessageId: number,
    customPayload?: UpdateEmailStatusPayload
  ): Promise<void> {
    try {
      if (customPayload) {
        await this.updateEmailStatus(mailMessageId, customPayload);
      } else {
        await this.updateEmailStatus(mailMessageId, {
          is_trash: true,
        });
      }
    } catch (error) {
      console.error("Error moving email to trash:", error);
      throw error;
    }
  }

  async archiveEmail(mailMessageId: number): Promise<void> {
    try {
      await this.updateEmailStatus(mailMessageId, {
        is_archive: true,
      });
    } catch (error) {
      console.error("Error archiving email:", error);
      throw error;
    }
  }

  async unarchiveEmail(mailMessageId: number): Promise<void> {
    try {
      await this.updateEmailStatus(mailMessageId, {
        is_archive: false,
      });
    } catch (error) {
      console.error("Error unarchiving email:", error);
      throw error;
    }
  }

  async markEmailAsUnread(mailMessageId: number): Promise<void> {
    try {
      await this.updateEmailStatus(mailMessageId, {
        is_read: false,
      });
    } catch (error) {
      console.error("Error marking email as unread:", error);
      throw error;
    }
  }

  async deleteEmail(mailMessageId: number): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      const tenantId = localStorage.getItem("selectedTenantId") || "";

      const response = await fetch(
        `${this.baseUrl}/mail-message/${mailMessageId}/?tenant_id=${tenantId}`,
        {
          method: "DELETE",
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      this.cachedEmails = this.cachedEmails.filter(
        (e) => e.mail_message_id !== mailMessageId
      );

      for (const pageData of Array.from(this.cachedEmailPages.values())) {
        if (pageData && Array.isArray(pageData.emails)) {
          pageData.emails = pageData.emails.filter((e: any) => e.mail_message_id !== mailMessageId);
        }
      }
    } catch (error) {
      console.error("Error deleting email:", error);
      throw error;
    }
  }

  async moveDraftToSent(mailMessageId: number): Promise<void> {
    try {
      const messageTypes = await this.getMailMessageTypes();
      const sentType = messageTypes.find(
        (mt) => mt.mail_message_type_key === "SENT"
      );

      if (!sentType) {
        throw new Error("Sent message type not found");
      }

      await this.updateEmailStatus(mailMessageId, {
        mail_message_type_id: sentType.mail_message_type_id,
      });
    } catch (error) {
      console.error("Error moving draft to sent:", error);
      throw error;
    }
  }

  async bulkMailOperation(payload: BulkMailOperationPayload): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      headers["Content-Type"] = "application/json";
      const tenantId = localStorage.getItem("selectedTenantId") || "";

      const response = await fetch(
        `${this.baseUrl}/bulk/mail-operation/?tenant_id=${tenantId}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      payload.mail_message_ids.forEach((mailMessageId) => {
        const idx = this.cachedEmails.findIndex(
          (e) => e.mail_message_id === mailMessageId
        );
        let deleted = false;
        if (idx !== -1) {
          const updatedEmail = { ...this.cachedEmails[idx] };

          switch (payload.operation) {
            case "mark_read":
              updatedEmail.is_read = true;
              break;
            case "mark_unread":
              updatedEmail.is_read = false;
              break;
            case "mark_important":
              updatedEmail.is_important = true;
              break;
            case "unmark_important":
              updatedEmail.is_important = false;
              break;
            case "trash":
              updatedEmail.is_trash = true;
              break;
            case "untrash":
              updatedEmail.is_trash = false;
              break;
            case "delete":
              this.cachedEmails.splice(idx, 1);
              deleted = true;
              break;
          }

          if (!deleted) {
            this.cachedEmails[idx] = updatedEmail;
          }
        }

        for (const pageData of Array.from(this.cachedEmailPages.values())) {
          if (pageData && Array.isArray(pageData.emails)) {
            const pageIdx = pageData.emails.findIndex((e: any) => e.mail_message_id === mailMessageId);
            if (pageIdx !== -1) {
              const updatedEmail = { ...pageData.emails[pageIdx] };
              let deletedPage = false;
              switch (payload.operation) {
                case "mark_read":
                  updatedEmail.is_read = true;
                  break;
                case "mark_unread":
                  updatedEmail.is_read = false;
                  break;
                case "mark_important":
                  updatedEmail.is_important = true;
                  break;
                case "unmark_important":
                  updatedEmail.is_important = false;
                  break;
                case "trash":
                  updatedEmail.is_trash = true;
                  break;
                case "untrash":
                  updatedEmail.is_trash = false;
                  break;
                case "delete":
                  pageData.emails.splice(pageIdx, 1);
                  deletedPage = true;
                  break;
              }

              if (!deletedPage) {
                pageData.emails[pageIdx] = updatedEmail;
              }
            }
          }
        }
      });
    } catch (error) {
      console.error("Error performing bulk mail operation:", error);
      throw error;
    }
  }

  async getMailStats(mailAuthId?: number): Promise<any> {
    try {
      if (mailAuthId) {
        const provider = this.cachedProviders?.find(p => p.mail_auth_id === mailAuthId);
        if (provider && provider.email_builtin_folder_stats) {
          return provider.email_builtin_folder_stats;
        }
        return { inbox: 0, important: 0, sentMail: 0, drafts: 0, spam: 0, trash: 0 };
      }

      const mergedStats = { inbox: 0, important: 0, sentMail: 0, drafts: 0, spam: 0, trash: 0 };
      if (this.cachedProviders) {
        this.cachedProviders.forEach(provider => {
          if (provider.email_builtin_folder_stats) {
            mergedStats.inbox += provider.email_builtin_folder_stats.inbox || 0;
            mergedStats.important += provider.email_builtin_folder_stats.important || 0;
            mergedStats.sentMail += provider.email_builtin_folder_stats.sentMail || 0;
            mergedStats.drafts += provider.email_builtin_folder_stats.drafts || 0;
            mergedStats.spam += provider.email_builtin_folder_stats.spam || 0;
            mergedStats.trash += provider.email_builtin_folder_stats.trash || 0;
          }
        });
      }
      return mergedStats;
    } catch (error) {
      console.error("Error getting mail stats:", error);
      return { inbox: 0, important: 0, sentMail: 0, drafts: 0, spam: 0, trash: 0 };
    }
  }
}

export default new EmailService();





